import type { AxiosResponse } from "axios";

/**
 * Builds the API URL with pagination, sorting, and filter query parameters.
 *
 * @param sortFieldMap Maps a TanStack column id to the backend OrderFilter
 *   property. When a column declares `sortField`, that value is used instead
 *   of the column id. Plan §4.7.2.
 */
export function buildApiUrl(
  resource: string,
  pagination: { pageIndex: number; pageSize: number },
  sorting: { id: string; desc: boolean }[],
  filters: Record<string, unknown>,
  globalFilter?: string,
  sortFieldMap: Record<string, string> = {},
): string {
  const params = new URLSearchParams();
  params.append("page", String(pagination.pageIndex + 1));
  params.append("itemsPerPage", String(pagination.pageSize));

  for (const sort of sorting) {
    const field = sortFieldMap[sort.id] ?? sort.id;
    params.append(`order[${field}]`, sort.desc ? "desc" : "asc");
  }

  for (const [key, value] of Object.entries(filters)) {
    if (Array.isArray(value)) {
      for (const item of value) {
        params.append(`${key}[]`, String(item));
      }
    } else if (value !== undefined && value !== null && value !== "") {
      params.append(key, String(value));
    }
  }

  if (globalFilter) {
    params.append("q", globalFilter);
  }

  return `${resource}?${params.toString()}`;
}

/**
 * Normalises an API Platform collection response.
 * Handles both Hydra format (with `member`) and plain arrays from custom providers.
 */
export function normalizeApiPlatformResponse<TData = Record<string, unknown>>(
  response: AxiosResponse<{ member?: TData[]; totalItems?: number; view?: string } | TData[]>,
): { member: TData[]; totalItems: number; view: string } {
  const data = response.data;
  const member = Array.isArray(data) ? data : (data?.member ?? []);
  return {
    member,
    totalItems: (data as { totalItems?: number })?.totalItems ?? member.length,
    view: (data as { view?: string })?.view ?? "default",
  };
}

/**
 * Combines initial server-side filters with TanStack Table column filters.
 * Column filters override initial filters on key collision.
 */
export function combineFilters(
  initialFilters: Record<string, unknown>,
  columnFilters: { id: string; value: unknown }[],
): Record<string, unknown> {
  const combined = { ...initialFilters };
  for (const filter of columnFilters) {
    combined[filter.id] = filter.value;
  }
  return combined;
}

/**
 * Capitalises the first letter of a string
 */
export function capitalize(str: string): string {
  return str ? str.charAt(0).toUpperCase() + str.slice(1) : "";
}
